import { useEffect, useRef, useState } from "react"
import { HeartFilled, HeartOutlined } from "@ant-design/icons"
import { Button, Tooltip, message } from "antd"
import type { ButtonProps } from "antd"
import {
  cancelOneClickFavorite,
  createOneClickFavorite,
  listOneClickFavorites,
} from "~/api/task"
import { ApiRequestError } from "~/common/request"
import { getTemplateAnalyticsFields } from "~/common/videoTemplate"
import { useSessionStore } from "~/store/session"
import { useVideoTemplate } from "~/store/videoTemplates"
import { useAnalyticsEvent } from "../Analytics"

type TemplateFavoriteButtonProps = {
  templateId: string
  category?: string
  defaultFavorite?: boolean
  onChange?: () => void
}

export const TemplateFavoriteButton = (props: TemplateFavoriteButtonProps) => {
  const user = useSessionStore((state) => state.user)
  return (
    <FavoriteButton
      key={`${user?.id ?? user?.mobile}:${user?.switch_user_id}:${props.templateId}`}
      {...props}
    />
  )
}

const FavoriteButton = ({
  templateId,
  category,
  defaultFavorite,
  onChange,
}: TemplateFavoriteButtonProps) => {
  const user = useSessionStore((state) => state.user)
  const openLoginModal = useSessionStore((state) => state.openLoginModal)
  const [favorite, setFavorite] = useState(defaultFavorite ?? false)
  const [loading, setLoading] = useState(
    Boolean(user && defaultFavorite === undefined),
  )
  const busy = useRef(false)
  const request = useRef<AbortController | null>(null)
  const { template: card } = useVideoTemplate(templateId)
  const sendAnalytics = useAnalyticsEvent({ category })
  const label = favorite ? "取消收藏" : "收藏该模板"

  const load = async (signal: AbortSignal) => {
    if (busy.current) return
    busy.current = true
    setLoading(true)
    try {
      const { data } = await listOneClickFavorites(
        { template_id: templateId, page: 0, page_size: 1 },
        { signal, notify: false },
      )
      if (signal.aborted) return
      setFavorite(
        Boolean(data?.items.some((item) => item.template_id === templateId)),
      )
    } catch {
      if (!signal.aborted) setFavorite(false)
    } finally {
      if (!signal.aborted) {
        busy.current = false
        setLoading(false)
      }
    }
  }

  const handleClick: ButtonProps["onClick"] = async (event) => {
    event.stopPropagation()
    if (category && user && !busy.current && !loading && !favorite) {
      sendAnalytics(
        "收藏视频",
        card ? getTemplateAnalyticsFields(card) : undefined,
      )
    }
    if (!user) {
      openLoginModal()
      return
    }
    const signal = request.current?.signal
    if (!signal || signal.aborted || busy.current) return
    busy.current = true
    setLoading(true)
    try {
      const update = favorite ? cancelOneClickFavorite : createOneClickFavorite
      const { data } = await update({ template_id: templateId }, { signal })
      if (signal.aborted) return
      if (!data || data.template_id !== templateId)
        throw new Error("收藏状态更新失败，请重试")
      setFavorite(!favorite)
      void message.success(favorite ? "已取消收藏" : "收藏成功")
      onChange?.()
    } catch (error) {
      if (!signal.aborted && !(error instanceof ApiRequestError))
        void message.error(
          error instanceof Error ? error.message : "收藏操作失败，请重试",
        )
    } finally {
      if (!signal.aborted) {
        busy.current = false
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    request.current = controller
    busy.current = false
    if (user && defaultFavorite === undefined) void load(controller.signal)
    return () => controller.abort()
  }, [])

  return (
    <Tooltip title={label}>
      <Button
        aria-label={label}
        icon={
          favorite ? (
            <HeartFilled style={{ color: "var(--ant-color-primary)" }} />
          ) : (
            <HeartOutlined />
          )
        }
        loading={loading}
        onClick={handleClick}
      />
    </Tooltip>
  )
}
